"use no memo";

// @tanstack/react-table v8 keeps its state in a mutable `table` instance whose identity is stable
// across updates, so React Compiler memoization would skip re-renders that the table needs. Remove
// this opt-out when the table upgrades to v9, which is compiler-safe.
import type { Row } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import Flags from "common/constants/flags";
import type { HTMLAttributes, ReactElement, ReactNode } from "react";
import React, { cloneElement, useEffect, useMemo } from "react";
import Link from "ui/components/Link";
import cx from "ui/cx";
import Checkbox from "ui/forms/Checkbox";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import useDraggableItem from "ui/hooks/useDraggableItem";
import IconHamburger from "ui/icons/IconHamburger";
import IconTriangleDownArrow from "ui/icons/IconTriangleDownArrow";
import { CLICKABLE_ELEMENTS } from "./constants";
import styles from "./DataTable.module.scss";
import type { DataTableRow } from "./types";

interface TableRowProps<T extends {}> extends DraggableProps, HTMLAttributes<HTMLDivElement> {
  row: Row<T>;
  href?: string;
  shiftMultiselect: ((row: Row<T>, shiftKey: boolean) => void) | null;
  overflowMenu?: (row: Row<T>) => ReactNode;
  expandableRow?: (row: Row<T>) => ReactNode;
  subTable?: (row: Row<T>) => ReactNode;
}

interface DraggableProps {
  index: number | undefined;
  moveRow?: (dragIndex: number, hoverIndex: number) => void;
  dropRow?: (dragIndex: number, hoverIndex: number) => void;
}

function DraggableTableRow<T extends {}>({
  index,
  moveRow,
  dropRow,
  onDragStart,
  onDrop,
  children,
  className,
  ...props
}: DraggableProps & HTMLAttributes<HTMLDivElement>) {
  const { dragRef, dropRef, isDragging, isOver } = useDraggableItem<Row<T>>({
    dragItem: moveRow,
    dropItem: dropRow,
    index: index ?? 0,
    dragItemType: "row",
  });

  return (
    <div
      ref={dropRef}
      onDrop={onDrop}
      // isDragging opacity works as a polyfill for mobile/touch preview (which doesnt work)
      style={isDragging ? { opacity: 0.5 } : undefined}
      className={cx(className, isOver && styles.dropTarget)}
      {...props}
    >
      {moveRow && (
        <div
          ref={dragRef}
          onDragStart={onDragStart}
          onClick={(e) => e.stopPropagation()}
          className={cx(styles.dummyCell, styles.draggable)}
        >
          <IconHamburger size={8} color="stone" />
        </div>
      )}
      {children}
    </div>
  );
}

const isChildOfClickable = (element: HTMLElement) => CLICKABLE_ELEMENTS.some((tagName) => element.closest(tagName));

export default function TableRow<T extends {}>({
  tableRef,
  row,
  index,
  onRowClick,
  moveRow,
  dropRow,
  getRowClassNames,
  isRowHighlighted,
  isRowSelected,
  onRowMouseEnter,
  onRowMouseLeave,
  getRowHref,
  subTable,
  parentTableWidth,
  expandableRow,
  rowStateClassNames = {},
  scrollIntoView,
  overflowMenu,
  shiftMultiselect,
}: TableRowProps<T> & DataTableRow<T> & { tableRef: HTMLDivElement | null }) {
  const selected = isRowSelected?.(row);
  useEffect(() => {
    // syncs react-table selection state with external
    // we may want to revisit how we handle row selection for more complex cases
    if (isRowSelected && row.getIsSelected() !== selected) {
      row.toggleSelected(selected);
    }
  }, [selected]);

  // due to colliding styles, only highlight if not selected
  const highlighted = !selected && isRowHighlighted?.(row);
  const href = getRowHref?.(row);
  const navigate = useAppNavigate();
  const expand =
    !!expandableRow || !!subTable || row.getIsGrouped()
      ? {
          canExpand: row.getCanExpand(),
          isExpanded: row.getIsExpanded(),
        }
      : null;
  const clickable = onRowClick || href || expand;

  useEffect(() => {
    if (expand && expand.isExpanded && !expand.canExpand) {
      row.toggleExpanded();
    }
  }, [expand?.canExpand]);

  // scroll selected table row into view
  useEffect(() => {
    if (tableRef && selected && scrollIntoView) {
      // TODO - check if needed
      // eg. scrollIntoView is HTMLElement (scroll container) and if row is already visible
      const element = tableRef.querySelector(`[data-row-id="${index}"]`);
      element?.scrollIntoView({ block: "start", inline: "nearest", behavior: "smooth" });
    }
  }, [selected, index]);

  const rowProps = {
    role: "row",
    ["data-row-id"]: index,
    ["data-clickable"]: clickable ? "true" : "false",
    className: cx(
      styles.row,
      highlighted && (rowStateClassNames.highlighted || styles.highlighted),
      clickable && styles.clickable,
      !selected && !highlighted && clickable && (rowStateClassNames.hover || styles.hover),
      selected && (rowStateClassNames.selected || styles.selected),
      getRowClassNames?.(row),
    ),
    onClick:
      onRowClick || href
        ? (e: React.MouseEvent) => {
            // Don't call click handler when clicking a clickable element
            const el = e.target as HTMLElement;
            if (!isChildOfClickable(el)) {
              // Fallback for when pointer-events: none is used on the row
              if (href) {
                void navigate({ to: href });
              }
              onRowClick?.(row, e);
            }
          }
        : undefined,
    onMouseEnter: onRowMouseEnter ? (e: React.MouseEvent) => onRowMouseEnter(row, e) : undefined,
    onMouseLeave: onRowMouseLeave ? (e: React.MouseEvent) => onRowMouseLeave(row, e) : undefined,
    moveRow,
    dropRow,
    index: moveRow ? index : undefined,
    // collapse on drag and drop to prevent funky UX
    onDragStart: moveRow && expand ? () => row.toggleExpanded(false) : undefined,
    onDrop: moveRow && expand ? () => row.toggleExpanded(false) : undefined,
  };

  if (expand) {
    rowProps.onClick = (e: React.MouseEvent) => {
      // Expand row except when clicking a clickable element
      const el = e.target as HTMLElement;
      if (!isChildOfClickable(el)) {
        // Fallback for when pointer-events: none is used on the row
        if (href) {
          void navigate({ to: href });
        } else {
          // Note that expansion and selection are not synced
          expand.canExpand && row.toggleExpanded();
        }
        onRowClick?.(row, e);
      }
    };
  }

  const { nestedSubTable, nestedShowsHeaders } = useMemo(() => {
    let nestedShowsHeaders = true;
    let nestedSubTable: ReactNode | null = null;
    if (subTable) {
      const original = subTable(row) as ReactElement<Record<string, unknown>>;
      // Allows conditionally rendering subTable (subTable={(row) => someCondition ? <DataTable /> : null})
      if (original) {
        nestedShowsHeaders = (original.props as { showColumnHeaders?: boolean }).showColumnHeaders ?? true;

        nestedSubTable = cloneElement(original, {
          parentTableWidth,
          parent: { rowData: row.original },
        });
      }
    }
    return { nestedSubTable, nestedShowsHeaders };
  }, [subTable, row, parentTableWidth]);

  const Row = moveRow ? DraggableTableRow : "div";

  const canMultiSelect = row.getCanMultiSelect();

  // Remove undefined keys (for "div")
  type RowPropsKey = keyof typeof rowProps;
  (Object.keys(rowProps) as RowPropsKey[]).forEach((key) => {
    if (rowProps[key] === undefined) {
      delete rowProps[key];
    }
  });
  return (
    <>
      <div className={styles.rowWrapper}>
        {/* Render the multiselect checkbox outside the table */}
        {canMultiSelect && (
          <div
            key={row.index + "_multiselect"}
            className={cx(styles.dummyCell, styles.multiSelectCell)}
            role="gridcell"
            data-column="multiselect"
          >
            <Checkbox
              checked={row.getIsSelected()}
              onClick={(e) => shiftMultiselect?.(row, e.shiftKey)}
              onChangeBoolean={() => row.toggleSelected()}
              disabled={!row.getCanSelect()}
              data-testid="multiselect-checkbox-input"
            />
          </div>
        )}
        <Row {...rowProps}>
          {/* This is a workaround for enabling row level anchor links without nesting links */}
          {href && (
            <Link
              key={row.index + "_href"}
              href={href}
              className={cx(styles.rowLink, Flags.TEST_E2E_MODE && styles.rowLinkE2E)}
            />
          )}

          {expand && (
            <div className={cx(styles.dummyCell, styles.expandCell)}>
              {expand.canExpand && (
                <Link onClick={() => row.toggleExpanded()} className={styles.expandIcon}>
                  <IconTriangleDownArrow rotate={expand.isExpanded ? 0 : -90} />
                </Link>
              )}
            </div>
          )}

          {row.getVisibleCells().map((cell) => (
            //  header added to key to disambiguate columns derived from same field
            <React.Fragment key={`${row.index}_${cell.column.columnDef.header?.toString() ?? ""}_${cell.id}`}>
              {cell.getIsPlaceholder() && <div className={cx(styles.dummyCell)}></div>}
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </React.Fragment>
          ))}
          {overflowMenu && (
            <div key={row.index + "_overflow"} className={cx(styles.overflowCell)} role="gridcell">
              {overflowMenu(row)}
            </div>
          )}
        </Row>
      </div>
      {!row.getIsGrouped() && expand?.isExpanded && (
        <div className={styles.rowWrapper}>
          {canMultiSelect && <div className={cx(styles.dummyCell, styles.multiSelectCell)} />}
          <div
            className={cx(
              rowStateClassNames.expanded,
              styles.expanded,
              !!subTable && styles.subTable,
              !!subTable && !nestedShowsHeaders,
            )}
          >
            {expandableRow?.(row)}
            {/* Hydrate the subTable with parent data and optionally hide headers */}
            {nestedSubTable}
          </div>
        </div>
      )}
    </>
  );
}
